<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { DEFAULT_LOCALE, pathFor, type Locale } from "@/seo/routes-manifest";

const { t } = useI18n();
const route = useRoute();

const currentLocale = computed<Locale>(
  () => (route.meta?.locale as Locale | undefined) ?? DEFAULT_LOCALE,
);

const homePath = computed(() => pathFor("home", currentLocale.value));
const philosophyPath = computed(() => pathFor("philosophy", currentLocale.value));
const featuresPath = computed(() => pathFor("features", currentLocale.value));

type FooterLinkTarget = { path: string; hash?: string } | string | null;

const columns = computed(() => [
  {
    title: t("footer.product"),
    links: [
      {
        label: t("footer.howItWorks"),
        to: { path: homePath.value, hash: "#problem" } as FooterLinkTarget,
      },
      { label: t("footer.themes"), to: { path: homePath.value, hash: "#themes" } },
      { label: t("footer.features"), to: featuresPath.value },
      { label: t("footer.philosophy"), to: philosophyPath.value },
      { label: t("footer.pricing"), to: { path: homePath.value, hash: "#pricing" } },
      { label: t("footer.device"), to: { path: homePath.value, hash: "#device" } },
    ],
  },
  {
    title: t("footer.company"),
    links: [
      { label: t("footer.about"), to: null as FooterLinkTarget },
      { label: t("footer.blog"), to: null },
    ],
  },
  {
    title: t("footer.legal"),
    links: [
      { label: t("footer.privacy"), to: "/privacy-policy" as FooterLinkTarget },
      { label: t("footer.terms"), to: "/terms-of-sale" },
    ],
  },
]);
</script>

<template>
  <footer class="bg-[var(--color-bg-dark)] px-5 pt-12 pb-8 md:px-12">
    <div class="mx-auto max-w-[var(--max-w-page)]">
      <!-- Link columns -->
      <div class="grid grid-cols-2 gap-8 md:grid-cols-3 md:gap-16">
        <div v-for="col in columns" :key="col.title">
          <p class="text-sm font-semibold text-white">{{ col.title }}</p>
          <ul class="mt-3 space-y-2.5">
            <li v-for="link in col.links" :key="link.label">
              <router-link
                v-if="link.to"
                :to="link.to"
                class="text-sm text-white/60 transition-colors hover:text-white"
              >
                {{ link.label }}
              </router-link>
              <a v-else href="#" class="text-sm text-white/60 transition-colors hover:text-white">
                {{ link.label }}
              </a>
            </li>
          </ul>
        </div>
      </div>

      <!-- Divider -->
      <hr class="my-8 border-white/10" />

      <!-- Bottom row -->
      <div
        class="flex flex-col items-center gap-4 text-center md:flex-row md:justify-between md:text-left"
      >
        <div>
          <p class="text-base font-bold text-white" style="font-family: var(--font-display)">
            cresci
          </p>
          <p class="mt-1 text-[13px] text-white/60">
            {{ t("footer.copyright") }}
          </p>
        </div>

        <!-- Social icons -->
        <div class="flex items-center gap-4">
          <a
            href="#"
            class="text-white/60 transition-colors hover:text-white"
            :aria-label="t('footer.twitter')"
          >
            <UIcon name="i-lucide-twitter" class="size-5" />
          </a>
          <a
            href="#"
            class="text-white/60 transition-colors hover:text-white"
            :aria-label="t('footer.instagram')"
          >
            <UIcon name="i-lucide-instagram" class="size-5" />
          </a>
        </div>
      </div>
    </div>
  </footer>
</template>
